/**
 * FromAddTable — table-chooser popover bound to `useTableSuggestions`. Only
 * mounts the TanStack Query subscription when an `editContext` is supplied,
 * so VQE-RO unit tests don't need a `QueryClientProvider` for `FromSection`.
 */

import { useMemo } from 'react';

import type { VqeEditContext } from '../../edit/editContext';
import { toCommitCtx } from '../../edit/editContext';
import { addTable } from '../../edit/edits/addTable';
import {
  tableSuggestionKey,
  tableSuggestionLabel,
} from '../../edit/edits/fromItemHelpers';
import { useTableSuggestions } from '../../edit/useSchemaSuggestions';
import { vqeCopy } from '../../ux/vqeCopy';
import { CommandPickerPopover, type CommandPickerItem } from '../common/CommandPickerPopover';

export interface FromAddTableProps {
  readonly editContext?: VqeEditContext;
  readonly itemCount: number;
  /**
   * Trigger label + test id. Defaults to the cross-join action on a FROM that
   * already has a table; the empty FROM row passes "Add table" — the same
   * `add-from-item` edit, which synthesizes the FROM clause when there is none.
   */
  readonly triggerLabel?: string;
  readonly testId?: string;
}

export function FromAddTable({
  editContext,
  itemCount,
  triggerLabel = vqeCopy.actions.addCrossJoin,
  testId = 'vqe-from-add-table',
}: FromAddTableProps) {
  if (!editContext) {
    return <CommandPickerPopover triggerLabel={triggerLabel} testId={testId} />;
  }
  return (
    <FromAddTableEnabled editContext={editContext} itemCount={itemCount} triggerLabel={triggerLabel} testId={testId} />
  );
}

interface EnabledProps {
  readonly editContext: VqeEditContext;
  readonly itemCount: number;
  readonly triggerLabel: string;
  readonly testId: string;
}

function FromAddTableEnabled({ editContext, itemCount, triggerLabel, testId }: EnabledProps) {
  const { tables, isLoading, hasSnapshot } = useTableSuggestions({
    service: editContext.service,
    scope: editContext.scope,
    cacheKey: `${editContext.documentId}::${editContext.statementId}`,
  });
  const items = useMemo<CommandPickerItem[]>(
    () =>
      tables.map((t) => ({
        key: tableSuggestionKey(t),
        label: tableSuggestionLabel(t),
      })),
    [tables],
  );
  const lookup = useMemo(() => {
    const map = new Map<string, (typeof tables)[number]>();
    for (const t of tables) map.set(tableSuggestionKey(t), t);
    return map;
  }, [tables]);

  const onSelect = (key: string) => {
    const table = lookup.get(key);
    if (!table) return;
    void addTable(table, toCommitCtx(editContext), {
      afterIndex: itemCount > 0 ? itemCount - 1 : undefined,
    });
  };

  const enabled = hasSnapshot && tables.length > 0;
  const disabledTooltip = hasSnapshot
    ? vqeCopy.disabled.addCrossJoinNoTables
    : vqeCopy.disabled.addCrossJoinNoSnapshot;
  return (
    <CommandPickerPopover
      triggerLabel={triggerLabel}
      testId={testId}
      disabled={!enabled}
      tooltip={enabled ? undefined : disabledTooltip}
      items={items}
      onSelect={enabled ? onSelect : undefined}
      loading={isLoading}
      emptyMessage={
        hasSnapshot
          ? 'No tables in scope. Refresh the schema or pick a database with tables.'
          : 'No schema cache yet — connect to a database to populate suggestions.'
      }
    />
  );
}
